import { Cross2Icon, FileTextIcon, UploadIcon } from '@radix-ui/react-icons';
import { useRef, useState, useCallback, KeyboardEvent, DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import cn from '../../../app/utils/cn';
import ProgressBar from '../ProgressBar';

interface IFileDropZoneProps {
  onFileSelected?: (file: File) => void;
  acceptedTypes: string[];
  acceptedExtensions: string[];
  maxSizeBytes: number;
  multiple?: boolean;
  onFilesSelected?: (files: File[]) => void;
  fileName?: string | null;
  onRemove?: () => void;
  isUploading?: boolean;
  progress?: number;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  hint?: string;
  inputLabel?: string;
  className?: string;
}

function hasAcceptedExtension(name: string, extensions: string[]): boolean {
  const lowerName = name.toLowerCase();
  return extensions.some((extension) =>
    lowerName.endsWith(extension.toLowerCase()),
  );
}

export default function FileDropZone({
  onFileSelected,
  acceptedTypes,
  acceptedExtensions,
  maxSizeBytes,
  multiple = false,
  onFilesSelected,
  fileName,
  onRemove,
  isUploading = false,
  progress = 0,
  error,
  disabled = false,
  placeholder,
  hint,
  inputLabel,
  className,
}: IFileDropZoneProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragRejected, setDragRejected] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);

  const validateFile = useCallback(
    (file: File): string | null => {
      const typeAccepted =
        acceptedTypes.includes(file.type) ||
        hasAcceptedExtension(file.name, acceptedExtensions);
      if (!typeAccepted) {
        return t('fileDropZone.errors.invalidType');
      }
      if (file.size > maxSizeBytes) {
        return t('fileDropZone.errors.tooLarge', {
          size: Math.round(maxSizeBytes / 1024 / 1024),
        });
      }
      return null;
    },
    [acceptedTypes, acceptedExtensions, maxSizeBytes, t],
  );

  const handleFileChange = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setInternalError(validationError);
        return;
      }
      setInternalError(null);
      onFileSelected?.(file);
    },
    [validateFile, onFileSelected],
  );

  const handleFilesChange = useCallback(
    (files: File[]) => {
      if (multiple && onFilesSelected) {
        const valid: File[] = [];
        let firstError: string | null = null;
        files.forEach((file) => {
          const validationError = validateFile(file);
          if (validationError) {
            if (!firstError) firstError = validationError;
          } else {
            valid.push(file);
          }
        });
        setInternalError(firstError);
        if (valid.length > 0) onFilesSelected(valid);
        return;
      }
      const file = files[0];
      if (!file) return;
      handleFileChange(file);
    },
    [multiple, onFilesSelected, validateFile, handleFileChange],
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled || isUploading) return;
      setIsDragOver(true);
      const item = e.dataTransfer.items[0];
      if (item && item.type && !acceptedTypes.includes(item.type)) {
        setDragRejected(true);
      } else {
        setDragRejected(false);
      }
    },
    [disabled, isUploading, acceptedTypes],
  );

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
    setDragRejected(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setDragRejected(false);
      if (disabled || isUploading) return;
      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;
      handleFilesChange(files);
    },
    [disabled, isUploading, handleFilesChange],
  );

  const handleClick = useCallback(() => {
    if (disabled || isUploading) return;
    inputRef.current?.click();
  }, [disabled, isUploading]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = '';
      if (files.length === 0) return;
      handleFilesChange(files);
    },
    [handleFilesChange],
  );

  const displayError = error ?? internalError;
  const hasFile = !!fileName;
  const interactive = !isUploading && !disabled;

  return (
    <div className={cn('w-full', className)}>
      <div
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-disabled={disabled}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative flex min-h-24 w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-center transition-colors duration-200',
          'border-border-input bg-surface-input',
          interactive && 'cursor-pointer',
          isDragOver &&
            !dragRejected &&
            'border-green-500 bg-green-50 dark:bg-green-900/20',
          dragRejected && 'border-red-500 bg-red-50 dark:bg-red-900/20',
          !!displayError && 'border-red-500',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={[...acceptedExtensions, ...acceptedTypes].join(',')}
          multiple={multiple}
          className="hidden"
          aria-label={inputLabel}
          onChange={handleInputChange}
          disabled={disabled}
        />

        {isUploading && (
          <div className="flex w-full flex-col items-center gap-2">
            <ProgressBar value={progress} showLabel className="w-full" />
            {onRemove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="bg-grey-800/70 text-white-0 hover:bg-grey-800/90 mt-1 flex h-10 w-10 items-center justify-center rounded-full"
                aria-label={t('fileDropZone.cancelLabel')}
              >
                <Cross2Icon className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {!isUploading && dragRejected && (
          <span className="text-sm text-red-500 dark:text-red-400">
            {t('fileDropZone.dragRejected')}
          </span>
        )}

        {!isUploading && !dragRejected && hasFile && (
          <div className="flex w-full flex-col items-center gap-2">
            <div className="text-text-secondary flex w-full min-w-0 items-center justify-center gap-2">
              <FileTextIcon className="text-text-tertiary size-5 shrink-0" />
              <span className="truncate text-sm font-medium break-all">
                {fileName}
              </span>
            </div>
            <span className="text-text-tertiary text-xs">
              {isDragOver
                ? t('fileDropZone.dropToReplace')
                : (placeholder ?? t('fileDropZone.placeholder'))}
            </span>
            {hint && <span className="text-text-tertiary text-xs">{hint}</span>}
            {onRemove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="text-text-tertiary hover:text-text-primary flex h-10 items-center justify-center gap-1 text-xs"
                aria-label={t('fileDropZone.removeLabel')}
              >
                <Cross2Icon className="h-3 w-3" />
                {t('fileDropZone.removeLabel')}
              </button>
            )}
          </div>
        )}

        {!isUploading && !dragRejected && !hasFile && (
          <div className="text-text-tertiary flex flex-col items-center gap-1">
            <UploadIcon className="h-8 w-8" />
            <span className="text-text-secondary text-sm font-medium">
              {placeholder ?? t('fileDropZone.placeholder')}
            </span>
            {hint && <span className="text-text-tertiary text-xs">{hint}</span>}
          </div>
        )}
      </div>

      {displayError && (
        <div className="mt-1 flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
          <span className="flex items-center gap-1">
            <Cross2Icon className="h-3 w-3" />
            <small>{displayError}</small>
          </span>
        </div>
      )}
    </div>
  );
}
